import { gql } from '@apollo/client';

/**
 * GraphQL operations for super-admin invocation of the staff-attribution
 * rollup. Both mutations are gated server-side via `requireSuperAdmin` in
 * `view-api/.../StaffAttributionAdmin.scala`; the client-side dialog
 * (`StaffAttributionAdminDialog`) additionally gates on
 * `useDebugMode().canBypassPermissions` (super-admin + debug mode) so the
 * trigger does not surface on any non-admin shell.
 *
 * The polling pattern used in `group-performance.ts` (run-status query)
 * is intentionally not replicated here: a `staffAttributionRollupRuns`
 * read API is deferred per IMPLEMENTATION_GUIDE.md (Stream 3). Until that
 * lands, success/failure is reported from the immediate
 * `BackfillInvokeResult` envelope returned by the mutation.
 */

export const INVOKE_STAFF_ATTRIBUTION_COORDINATOR = gql`
  mutation InvokeStaffAttributionCoordinator($dryRun: Boolean!) {
    invokeStaffAttributionCoordinator(dryRun: $dryRun) {
      success
      runId
      message
    }
  }
`;

export const INVOKE_STAFF_ATTRIBUTION_BACKFILL = gql`
  mutation InvokeStaffAttributionBackfill(
    $backfillDays: Int!
    $backfillMode: String!
    $targetDate: String
    $dryRun: Boolean!
  ) {
    invokeStaffAttributionBackfill(
      backfillDays: $backfillDays
      backfillMode: $backfillMode
      targetDate: $targetDate
      dryRun: $dryRun
    ) {
      success
      runId
      message
    }
  }
`;

export interface StaffAttributionInvokeResult {
  success: boolean;
  runId: string | null;
  message: string;
}

export interface InvokeStaffAttributionCoordinatorData {
  invokeStaffAttributionCoordinator: StaffAttributionInvokeResult;
}

export interface InvokeStaffAttributionCoordinatorVars {
  dryRun: boolean;
}

export interface InvokeStaffAttributionBackfillData {
  invokeStaffAttributionBackfill: StaffAttributionInvokeResult;
}

export type StaffAttributionBackfillMode = 'missing_only' | 'overwrite_all';

export interface InvokeStaffAttributionBackfillVars {
  backfillDays: number;
  backfillMode: StaffAttributionBackfillMode;
  /**
   * End date of the window (`yyyy-MM-dd`, org TZ). When `null`/omitted,
   * the Lambda defaults to yesterday in the org timezone — matching the
   * historical "backfillDays back from today" behavior.
   */
  targetDate: string | null;
  dryRun: boolean;
}
